import { SignupFlow, SignupFlowType, SignupScreen } from 'fitbeat.gym-core';
import { homePlanEnvironmentMapForWebFlow } from 'fitbeat.models';
import produce from 'immer';
import { getConfig } from '../../config';
import { OPEN_HOME_WORKOUT_REQUEST } from '../../HomeWorkouts/actions';
import { i18n } from '../../utils';
import * as actionTypes from '../actionTypes';
import {
  ADD_AVATAR_FAILURE,
  ADD_AVATAR_NEXT_PRESSED_FAILURE,
  ADD_AVATAR_NEXT_PRESSED_REQUEST,
  ADD_AVATAR_NEXT_PRESSED_SUCCESS,
  ADD_AVATAR_REQUEST,
  ADD_AVATAR_SCREEN_MOUNTED_FAILURE,
  ADD_AVATAR_SCREEN_MOUNTED_REQUEST,
  ADD_AVATAR_SCREEN_MOUNTED_SUCCESS,
  ADD_AVATAR_SUCCESS,
  ADD_AVATAR_UNMOUNTED,
  SAVE_AVATAR_PRESS_FAILURE,
  SAVE_AVATAR_PRESS_REQUEST,
  SAVE_AVATAR_PRESS_SUCCESS,
} from '../app-onboarding/capture-avatar-actions';
import { ISignupState, SignupActionTypes } from '../types';

const environment = getConfig().gymEnvironment;

export const initialSignupState: ISignupState = {
  isLoading: false,
  screens: SignupFlow.getScreenForSignupFlowType(SignupFlowType.TypeOne),
  signupScreenWithIndex: {
    screen: SignupScreen.Start,
    index: -1,
  },
  iap: {
    isLoading: false,
    error: undefined,
    product: undefined,
    activeProduct: undefined,
  },
  userProfile: undefined,
  isUpdatingUserProfile: false,
  updatingUserProfileError: undefined,
  openInstantWorkout: false,
  shouldPersistUser: false,
  inviteAcceptStatus: 'reject',
  defaultHomePlanId: homePlanEnvironmentMapForWebFlow[environment],
  stripeHomePlanSummary: {
    isLoading: false,
    error: undefined,
    summary: undefined,
  },
  captureEmailLoading: false,
  preAccountSetPasswordLoading: false,
  userExistsCheckData: undefined,
  captureEmailError: undefined,
  preAccountSetPasswordScreenLoading: false,
  userProfileFieldsToUpdateAfterOnboarding: {},
  uploadingAvatar: false,
};

export const signup = produce(
  (draft: ISignupState = initialSignupState, action: SignupActionTypes): ISignupState => {
    switch (action.type) {
      case actionTypes.SIGNUP_FLOW_SCREEN_REQUEST:
        draft.isLoading = true;
        break;
      case actionTypes.SIGNUP_FLOW_SCREEN_SUCCESS:
        const hasSignupProcessStarted = draft.signupScreenWithIndex.index > -1;
        if (action.payload.length > 0 && !hasSignupProcessStarted) {
          draft.screens = action.payload;
        }
        draft.isLoading = false;
        break;
      case actionTypes.SIGNUP_FLOW_SCREEN_FAILURE:
        draft.isLoading = false;
        draft.screens = initialSignupState.screens;
        break;
      case actionTypes.APP_ONBOARDING_STARTED:
        draft.userProfile = action.payload.userProfile;
        draft.enabledFeatures = action.payload.enabledFeatures;
        break;
      case actionTypes.NEXT_SCREEN_STEP:
        const {
          currentScreen, skipRestOfFlow, userProfile: userProfileUpdateFields, inviteAcceptStatus,
          gym, options,
        } = action.payload;
        draft.userProfileFieldsToUpdateAfterOnboarding = {
          ...draft.userProfileFieldsToUpdateAfterOnboarding,
          ...userProfileUpdateFields,
        };
        draft.signupScreenWithIndex = SignupFlow.getNextScreen(
          draft.userProfile!,
          currentScreen,
          draft.screens,
          draft.enabledFeatures!,
          gym,
          skipRestOfFlow,
          options,
        );
        draft.isLoading = true;
        draft.shouldPersistUser = draft.signupScreenWithIndex.screen === SignupScreen.DashboardTabNavigator;
        draft.isUpdatingUserProfile = draft.shouldPersistUser;
        draft.updatingUserProfileError = undefined;
        draft.inviteAcceptStatus = inviteAcceptStatus || draft.inviteAcceptStatus;
        break;
      case actionTypes.UPDATE_USER_PROFILE_SUCCESS:
        draft.updatingUserProfileError = undefined;
        draft.isUpdatingUserProfile = false;
        break;
      case actionTypes.UPDATE_USER_PROFILE_FAILURE:
        draft.updatingUserProfileError = action.payload;
        draft.isUpdatingUserProfile = false;
        break;
      case actionTypes.ON_SCREEN_COMPLETE:
        draft.isLoading = false;
        draft.openInstantWorkout = action.payload;
        break;
      case actionTypes.IAP_PRODUCTS_REQUEST:
        draft.iap.isLoading = true;
        draft.iap.error = undefined;
        draft.iap.product = undefined;
        break;
      case actionTypes.IAP_PRODUCTS_SUCCESS:
        const { productSku, productsForSale, activeProducts } = action.payload;
        const product = productsForSale.find((p) => p.sku === productSku);
        let error;
        let matchingActiveProduct;
        if (!product) {
          matchingActiveProduct = activeProducts.find((p) => p.sku === productSku);
          if (matchingActiveProduct) {
            error = i18n.t('auth:paymentSuccessfulWebhookFailed');
          } else {
            error = `No product found matching: ${productSku}`;
          }
        }
        draft.iap.isLoading = false;
        draft.iap.error = error;
        draft.iap.product = product;
        draft.iap.activeProduct = matchingActiveProduct;
        break;
      case actionTypes.IAP_PRODUCTS_FAILURE:
        draft.iap.error = action.payload;
        draft.iap.isLoading = false;
        break;
      case actionTypes.IAP_BUY_REQUEST:
        draft.iap.error = undefined;
        draft.iap.isLoading = true;
        break;
      case actionTypes.IAP_BUY_SUCCESS:
        draft.iap.error = undefined;
        draft.iap.isLoading = false;
        break;
      case actionTypes.IAP_BUY_FAILURE:
        draft.iap.error = action.payload;
        draft.iap.isLoading = false;
        break;
      case OPEN_HOME_WORKOUT_REQUEST:
        draft.openInstantWorkout = false;
        break;
      case actionTypes.STRIPE_HOME_PLAN_SUMMARY_REQUEST:
        draft.stripeHomePlanSummary.isLoading = true;
        draft.stripeHomePlanSummary.error = undefined;
        draft.stripeHomePlanSummary.summary = undefined;
        break;
      case actionTypes.STRIPE_HOME_PLAN_SUMMARY_SUCCESS:
        draft.stripeHomePlanSummary.isLoading = false;
        draft.stripeHomePlanSummary.error = undefined;
        draft.stripeHomePlanSummary.summary = action.payload;
        break;
      case actionTypes.STRIPE_HOME_PLAN_SUMMARY_FAILURE:
        draft.stripeHomePlanSummary.isLoading = false;
        draft.stripeHomePlanSummary.error = action.payload;
        draft.stripeHomePlanSummary.summary = undefined;
        break;
      case actionTypes.CAPTURE_EMAIL_SUBMIT_REQUEST:
        draft.captureEmailLoading = true;
        draft.captureEmailError = undefined;
        break;
      case actionTypes.CAPTURE_EMAIL_SUBMIT_SUCCESS:
        draft.captureEmailLoading = false;
        draft.userExistsCheckData = action.payload?.userExistsCheck;
        draft.captureEmailError = undefined;
        break;
      case actionTypes.CAPTURE_EMAIL_SUBMIT_FAILURE:
        draft.captureEmailLoading = false;
        draft.captureEmailError = action.payload?.error;
        break;
      case actionTypes.PRE_ACCOUNT_SET_PASSWORD_REQUEST:
        draft.preAccountSetPasswordLoading = true;
        break;
      case actionTypes.PRE_ACCOUNT_SET_PASSWORD_SUCCESS:
        draft.preAccountSetPasswordLoading = false;
        break;
      case actionTypes.PRE_ACCOUNT_SET_PASSWORD_FAILURE:
        draft.preAccountSetPasswordLoading = false;
        break;
      case actionTypes.SET_PASSWORD_SCREEN_MOUNTED_REQUEST:
        draft.preAccountSetPasswordScreenLoading = true;
        break;
      case actionTypes.SET_PASSWORD_SCREEN_MOUNTED_SUCCESS:
        draft.preAccountSetPasswordScreenLoading = false;
        draft.userExistsCheckData = action.payload?.userExistsCheck;
        break;
      case actionTypes.SET_PASSWORD_SCREEN_MOUNTED_FAILURE:
        draft.preAccountSetPasswordScreenLoading = false;
        break;
      case actionTypes.INSTALL_APP_SCREEN_MOUNTED_REQUEST:
        draft.installAppScreenLoading = true;
        break;
      case actionTypes.INSTALL_APP_SCREEN_MOUNTED_SUCCESS:
        draft.installAppScreenLoading = false;
        draft.continueSignupUserProfile = action.payload.userProfile;
        break;
      case actionTypes.INSTALL_APP_SCREEN_MOUNTED_FAILURE:
        draft.installAppScreenLoading = false;
        break;
      case ADD_AVATAR_REQUEST:
        draft.uploadingAvatarErrorMessage = undefined;
        break;
      case ADD_AVATAR_SUCCESS:
        draft.uploadedAvatar = action.payload.uploadedAvatar;
        draft.uploadingAvatarErrorMessage = undefined;
        break;
      case ADD_AVATAR_FAILURE:
        draft.uploadingAvatarErrorMessage = action.payload.errorMessage;
        break;
      case ADD_AVATAR_NEXT_PRESSED_REQUEST:
        draft.uploadingAvatar = true;
        draft.uploadingAvatarErrorMessage = undefined;
        break;
      case ADD_AVATAR_NEXT_PRESSED_SUCCESS:
        draft.uploadingAvatar = false;
        draft.uploadingAvatarErrorMessage = undefined;
        break;
      case ADD_AVATAR_NEXT_PRESSED_FAILURE:
        draft.uploadingAvatar = false;
        draft.uploadingAvatarErrorMessage = action.payload.errorMessage;
        break;
      case ADD_AVATAR_SCREEN_MOUNTED_REQUEST:
        draft.loadingExistingAvatar = true;
        break;
      case ADD_AVATAR_SCREEN_MOUNTED_SUCCESS:
        draft.loadingExistingAvatar = false;
        draft.existingAvatar = action.payload.existingAvatar;
        break;
      case ADD_AVATAR_SCREEN_MOUNTED_FAILURE:
        draft.loadingExistingAvatar = false;
        draft.uploadingAvatarErrorMessage = action.payload.errorMessage;
        break;
      case SAVE_AVATAR_PRESS_REQUEST:
        draft.uploadingAvatar = true;
        delete draft.uploadingAvatarErrorMessage;
        break;
      case SAVE_AVATAR_PRESS_SUCCESS:
        draft.uploadingAvatar = false;
        draft.existingAvatar = draft.uploadedAvatar;
        break;
      case SAVE_AVATAR_PRESS_FAILURE:
        draft.uploadingAvatar = false;
        draft.uploadingAvatarErrorMessage = action.payload.errorMessage;
        break;
      case ADD_AVATAR_UNMOUNTED:
        delete draft.uploadedAvatar;
        break;
    }
    return draft;
  },
);
